import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { X, Upload, Edit, ArrowLeft } from 'lucide-react';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious,
  PaginationEllipsis 
} from './ui/pagination';
import { toast } from 'sonner';
import { MetadataItem } from './MetadataUploadForm';
import { ImageWithFallback } from './figma/ImageWithFallback';

interface MetadataEditFormProps {
  existingMetadata: MetadataItem[];
  onUpdate: (id: string, updates: Partial<MetadataItem>) => void;
}

export function MetadataEditForm({ existingMetadata, onUpdate }: MetadataEditFormProps) {
  const [editMode, setEditMode] = useState(false);
  const [selectedEditItem, setSelectedEditItem] = useState<MetadataItem | null>(null);

  // Pagination for existing metadata
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(existingMetadata.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMetadata = existingMetadata.slice(startIndex, endIndex);

  const loadExistingForEdit = (item: MetadataItem) => {
    setEditMode(true);
    setSelectedEditItem(item);
  };

  const handleEditSubmit = () => {
    if (!selectedEditItem) return;

    onUpdate(selectedEditItem.id, {
      name: selectedEditItem.displayName, // Use displayName as name
      displayName: selectedEditItem.displayName,
      imageType: selectedEditItem.imageType,
      campaignTypes: selectedEditItem.campaignTypes,
      categories: selectedEditItem.categories
      // fileName is not included - it should not be editable after upload
    });

    toast.success('Metadata updated successfully');
    setEditMode(false);
    setSelectedEditItem(null);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setSelectedEditItem(null);
  };

  const updateEditItem = (updates: Partial<MetadataItem>) => {
    if (selectedEditItem) {
      setSelectedEditItem({ ...selectedEditItem, ...updates });
    }
  };

  const addEditTag = (field: 'campaignTypes' | 'categories', value: string) => {
    if (!selectedEditItem || !value) return;
    
    const currentArray = selectedEditItem[field];
    if (!currentArray.includes(value)) {
      updateEditItem({
        [field]: [...currentArray, value]
      });
    }
  };

  const removeEditTag = (field: 'campaignTypes' | 'categories', value: string) => {
    if (!selectedEditItem) return;
    
    updateEditItem({
      [field]: selectedEditItem[field].filter(v => v !== value)
    });
  };

  // Edit Mode View
  if (editMode && selectedEditItem) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Button variant="ghost" size="sm" onClick={cancelEdit}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to List
            </Button>
          </div>
          <CardTitle>Edit Metadata</CardTitle>
          <CardDescription>Update the metadata for the selected item</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Preview */}
          <div className="space-y-4">
            <Label>Image Preview</Label>
            <div className="flex gap-4 items-start border rounded-lg p-4">
              <ImageWithFallback
                src={`https://picsum.photos/seed/${selectedEditItem.fileName}/400/300`}
                alt={selectedEditItem.displayName}
                className="w-32 h-32 object-cover rounded flex-shrink-0"
              />
              <div className="flex-1 space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-displayName">Display Name *</Label>
                  <Input
                    id="edit-displayName"
                    placeholder="e.g., Premium Black Leather Texture"
                    value={selectedEditItem.displayName}
                    onChange={(e) => updateEditItem({ displayName: e.target.value })}
                  />
                </div>
                <p className="text-xs text-gray-500">File: {selectedEditItem.fileName}</p>
              </div>
            </div>
          </div>

          {/* Image Type */}
          <div className="space-y-2">
            <Label htmlFor="edit-type">Image Type *</Label>
            <Select
              value={selectedEditItem.imageType}
              onValueChange={(value: 'background' | 'foreground') => 
                updateEditItem({ imageType: value })
              }
            >
              <SelectTrigger id="edit-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="background">Background</SelectItem>
                <SelectItem value="foreground">Foreground</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Campaign Types */}
          <div className="space-y-2">
            <Label>Campaign Types *</Label>
            <Input
              placeholder="Type and press Enter to add"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addEditTag('campaignTypes', e.currentTarget.value);
                  e.currentTarget.value = '';
                }
              }}
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedEditItem.campaignTypes.map(tag => (
                <Badge key={tag} variant="secondary">
                  {tag}
                  <button
                    onClick={() => removeEditTag('campaignTypes', tag)}
                    className="ml-2 hover:text-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-2">
            <Label>Categories *</Label>
            <Input
              placeholder="Type and press Enter to add"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addEditTag('categories', e.currentTarget.value);
                  e.currentTarget.value = '';
                }
              }}
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedEditItem.categories.map(tag => (
                <Badge key={tag} variant="secondary">
                  {tag}
                  <button
                    onClick={() => removeEditTag('categories', tag)}
                    className="ml-2 hover:text-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button onClick={handleEditSubmit}>
              <Upload className="w-4 h-4 mr-2" />
              Update Metadata
            </Button>
            <Button variant="outline" onClick={cancelEdit}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // List View - Shows all existing items to select for editing
  return (
    <Card>
      <CardHeader>
        <CardTitle>Edit Existing Metadata</CardTitle>
        <CardDescription>
          Select an item to edit its metadata ({existingMetadata.length} total items)
        </CardDescription>
      </CardHeader>
      <CardContent>
        {existingMetadata.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No metadata entries found. Upload some images to get started!
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {paginatedMetadata.map(item => (
                <div key={item.id} className="border rounded-lg p-4 hover:border-gray-400 transition-colors">
                  <ImageWithFallback
                    src={`https://picsum.photos/seed/${item.fileName}/400/300`}
                    alt={item.name}
                    className="w-full h-32 object-cover rounded mb-3"
                  />
                  <div className="mb-3 text-sm text-gray-900 font-medium">{item.displayName}</div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="w-full"
                    onClick={() => loadExistingForEdit(item)}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentPage(Math.max(1, currentPage - 1));
                      }}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <PaginationItem key={page}>
                          <PaginationLink
                            isActive={currentPage === page}
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(page);
                            }}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    } else if (
                      page === currentPage - 2 ||
                      page === currentPage + 2
                    ) {
                      return (
                        <PaginationItem key={page}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      );
                    }
                    return null;
                  })}

                  <PaginationItem>
                    <PaginationNext 
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentPage(Math.min(totalPages, currentPage + 1));
                      }}
                      className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
